import { useState, useMemo, useEffect } from 'react'
import * as XLSX from 'xlsx'
import Modal from '../Modal'
import DateRangeFilter from '../DateRangeFilter'
import * as api from '../../lib/api'
import { useToast } from '../../contexts/ToastContext'
import { useAuth } from '../../contexts/AuthContext'
import CustomSelect from '../CustomSelect/CustomSelect'

/**
 * Универсальный мастер импорта (Версия 2.2 с анализом периода и настройкой года).
 */
export default function ImportWizard({ 
  isOpen, 
  onClose, 
  employees, 
  onComplete,
  targetTable = 'work_logs',
  fields = [],
  pvzId = null 
}) {
  const { profile } = useAuth()
  const toast = useToast()
  const [step, setStep] = useState('upload')
  const [loading, setLoading] = useState(false)
  const [workbook, setWorkbook] = useState(null)
  const [sheetNames, setSheetNames] = useState([])
  const [selectedSheet, setSelectedSheet] = useState('')
  const [headerRow, setHeaderRow] = useState(1)
  const [defaultYear, setDefaultYear] = useState(() => new Date().getFullYear())
  const [rawRows, setRawRows] = useState([])
  const [mapping, setMapping] = useState(() => {
    const initial = {}
    fields.forEach(f => initial[f.id] = '')
    return initial
  })
  const [customValues, setCustomValues] = useState({})
  const [employeeMap, setEmployeeMap] = useState({})
  const [previewData, setPreviewData] = useState([])
  const [importId] = useState(() => `IMP-${Date.now()}`)
  const [importStartDate, setImportStartDate] = useState('')
  const [importEndDate, setImportEndDate] = useState('')
  const [importMarkers, setImportMarkers] = useState(null)

  useEffect(() => {
    let active = true
    const loadMarkersData = async () => {
      if (!pvzId) return
      
      try {
        const now = new Date()
        // Возьмем период пошире: 90 дней назад и 30 дней вперед
        const startDateObj = new Date()
        startDateObj.setDate(now.getDate() - 90)
        const endDateObj = new Date()
        endDateObj.setDate(now.getDate() + 30)
        
        const startStr = startDateObj.toISOString().split('T')[0]
        const endStr = endDateObj.toISOString().split('T')[0]
        
        let rawData = []
        if (targetTable === 'wb_incomes') {
          rawData = await api.getWbIncomes(pvzId, startStr, endStr)
        } else if (targetTable === 'expenses') {
          rawData = await api.getExpenses(pvzId, startStr, endStr)
        } else if (targetTable === 'work_logs') {
          rawData = await api.getWorkLogs('all', { startDate: startStr, endDate: endStr, pvzId })
        } else if (targetTable === 'payments') {
          rawData = await api.getPayments({ periodStart: startStr, periodEnd: endStr, pvzId })
        }
        
        if (!active) return

        // Группируем по датам
        const markers = {}
        
        if (targetTable === 'wb_incomes') {
          rawData.forEach(item => {
            const d = item.period_start || item.date
            if (!d) return
            if (!markers[d]) markers[d] = []
            if (!markers[d].some(m => m.name === 'ВБ')) {
              markers[d].push({ name: 'ВБ', filled: true })
            }
          })
        } else if (targetTable === 'expenses') {
          rawData.forEach(item => {
            const d = item.date
            if (!d) return
            if (!markers[d]) markers[d] = []
            if (!markers[d].some(m => m.name === 'Рсх')) {
              markers[d].push({ name: 'Рсх', filled: true })
            }
          })
        } else if (targetTable === 'work_logs') {
          rawData.forEach(item => {
            const d = item.work_date
            if (!d) return
            if (!markers[d]) markers[d] = []
            // Чтобы не перегружать календарь текстом, используем первую букву имени сотрудника
            const shortName = item.employee_name ? item.employee_name.split(' ')[0] : 'Смена'
            markers[d].push({ 
              name: shortName, 
              filled: true 
            })
          })
        } else if (targetTable === 'payments') {
          rawData.forEach(item => {
            const d = item.period_start || item.date
            if (!d) return
            if (!markers[d]) markers[d] = []
            const typeName = item.type === 'fine' ? 'Штрф' : 'Прм'
            if (!markers[d].some(m => m.name === typeName)) {
              markers[d].push({ 
                name: typeName, 
                filled: true 
              })
            }
          })
        }
        
        setImportMarkers(markers)
      } catch (e) {
        console.error('Ошибка загрузки маркеров для импорта:', e)
      }
    }
    
    loadMarkersData()
    return () => {
      active = false
    }
  }, [targetTable, pvzId])

  const [loadingExisting, setLoadingExisting] = useState(false)
  const [dbItems, setDbItems] = useState([])
  const [duplicateAction, setDuplicateAction] = useState('skip') // 'skip', 'overwrite', 'keep_both'
  const [filterType, setFilterType] = useState('all') // 'all', 'new', 'dup'

  // Обнаружение дубликатов
  const duplicates = useMemo(() => {
    if (!dbItems || dbItems.length === 0 || previewData.length === 0) return []
    
    return previewData.filter(d => {
      if (targetTable === 'wb_incomes') {
        return dbItems.some(db => 
          db.period_start === d.period_start && 
          db.period_end === d.period_end && 
          (db.source || 'Wildberries') === (d.source || 'Wildberries')
        )
      }
      if (targetTable === 'expenses') {
        return dbItems.some(db => 
          db.date === (d.date || d.period_end) && 
          Number(db.amount) === Number(d.amount) && 
          db.category === d.category
        )
      }
      if (targetTable === 'work_logs') {
        return dbItems.some(db => 
          db.employee_id === d.targetId && 
          db.work_date === d.date && 
          db.type === d.type
        )
      }
      if (targetTable === 'payments') {
        const dbType = d.type === 'fines' ? 'fine' : 'bonus'
        return dbItems.some(db => 
          db.employee_id === d.targetId && 
          db.period_start === d.date && 
          db.type === dbType && 
          Number(db.amount) === Number(d.amount)
        )
      }
      return false
    })
  }, [previewData, dbItems, targetTable])

  // Отфильтрованные данные для предпросмотра
  const filteredPreviewData = useMemo(() => {
    return previewData.filter(d => {
      const isDup = duplicates.some(dup => dup.id === d.id)
      if (filterType === 'new') return !isDup
      if (filterType === 'dup') return isDup
      return true
    })
  }, [previewData, duplicates, filterType])

  // Статистика импорта
  const stats = useMemo(() => {
    if (previewData.length === 0) return null
    const dates = previewData.map(d => d.date || d.period_start).filter(Boolean).sort()
    return {
      count: previewData.length,
      start: dates[0],
      end: dates[dates.length - 1]
    }
  }, [previewData])

  const stepInfo = {
    upload: { num: 1, title: 'Загрузка файла' },
    sheet: { num: 2, title: 'Настройка листа' },
    columns: { num: 3, title: 'Колонки данных' },
    matching: { num: 4, title: 'Сотрудники' },
    review: { num: 5, title: 'Проверка' }
  }

  // 1. Файл
  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setLoading(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const data = evt.target.result
        const wb = XLSX.read(data, { type: 'binary' })
        setWorkbook(wb)
        setSheetNames(wb.SheetNames)
        setSelectedSheet(wb.SheetNames[0])
        setStep('sheet')
      } catch (err) {
        toast.error('Ошибка чтения файла')
      } finally {
        setLoading(false)
      }
    }
    reader.readAsBinaryString(file)
  }

  // 2. Лист
  const handleSheetSelect = () => {
    const worksheet = workbook.Sheets[selectedSheet]
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' })
    setRawRows(jsonData)
    setStep('columns')
  }

  const columns = useMemo(() => {
    if (rawRows.length < headerRow) return []
    return rawRows[headerRow - 1].map((c, i) => ({ label: String(c || `Колонка ${i+1}`), index: i }))
  }, [rawRows, headerRow])

  // Автоматический маппинг колонок Wildberries
  useEffect(() => {
    if (columns.length > 0) {
      const newMapping = { ...mapping }
      let changed = false
      columns.forEach(c => {
        const lbl = c.label.toLowerCase()
        if (lbl === 'дата') {
          if (newMapping.period_start === '') { newMapping.period_start = c.index; changed = true; }
          if (newMapping.period_end === '') { newMapping.period_end = c.index; changed = true; }
          if ('date' in newMapping && newMapping.date === '') { newMapping.date = c.index; changed = true; }
        }
        if (lbl === 'итог') {
          if (newMapping.amount === '') { newMapping.amount = c.index; changed = true; }
        }
        if (lbl === 'оборот') {
          if ('turnover' in newMapping && newMapping.turnover === '') { newMapping.turnover = c.index; changed = true; }
        }
        if (lbl === 'продажа по тарифу' || lbl === 'начислено по тарифу') {
          if ('sale_by_tariff' in newMapping && newMapping.sale_by_tariff === '') { newMapping.sale_by_tariff = c.index; changed = true; }
        }
      })
      if (changed) setMapping(newMapping)
    }
  }, [columns])

  // 3. Колонки
  const handleMappingConfirm = () => {
    const missing = fields.filter(f => f.required && mapping[f.id] === '' && !customValues[f.id])
    if (missing.length > 0) {
      toast.error(`Укажите колонки или впишите значения для: ${missing.map(m => m.label).join(', ')}`)
      return
    }

    if (targetTable === 'work_logs' || targetTable === 'payments') {
      const names = new Set()
      const dataRows = rawRows.slice(headerRow)
      dataRows.forEach(row => {
        const name = String(row[mapping.employee] || '').trim()
        if (name) names.add(name)
      })

      const initialMap = {}
      names.forEach(name => {
        const found = employees.find(e => e.name.toLowerCase() === name.toLowerCase())
        initialMap[name] = found ? found.id : 'new'
      })
      setEmployeeMap(initialMap)
      setStep('matching')
    } else {
      // Для Аналитики пропускаем шаг сопоставления сотрудников
      handleMatchingConfirm()
    }
  }

  // 4. Предварительный расчет данных
  const handleMatchingConfirm = async () => {
    setLoadingExisting(true)
    const dataRows = rawRows.slice(headerRow)
    const finalized = []

    dataRows.forEach((row, idx) => {
      // Для логов и платежей нужен сотрудник
      const rawName = targetTable === 'work_logs' || targetTable === 'payments' 
        ? String(row[mapping.employee] || '').trim() 
        : 'System'
      
      if ((targetTable === 'work_logs' || targetTable === 'payments') && !rawName) return

      // Обработка дат (одинарная или период)
      const date = mapping.date ? parseExcelDate(row[mapping.date], defaultYear) : null
      const pStart = mapping.period_start ? parseExcelDate(row[mapping.period_start], defaultYear) : date
      const pEnd = mapping.period_end ? parseExcelDate(row[mapping.period_end], defaultYear) : date
      
      const compDate = date || pStart
      const compDateEnd = date || pEnd
      if (importStartDate && compDate && compDate < importStartDate) return
      if (importEndDate && compDateEnd && compDateEnd > importEndDate) return

      const comment = mapping.comment ? String(row[mapping.comment] || '') : ''

      if (targetTable === 'work_logs' || targetTable === 'payments') {
        Object.keys(mapping).forEach(fieldId => {
          if (!fieldId.endsWith('_col')) return
          const colIndex = mapping[fieldId]
          if (colIndex === '') return

          let valStr = String(row[colIndex] || '0')
          const val = parseFloat(valStr.replace(/[^\d.,-]/g, '').replace(',', '.'))
          if (val <= 0 || isNaN(val)) return

          const type = fieldId.replace('_col', '')
          finalized.push({
            id: `${idx}-${type}`,
            date,
            employeeName: rawName,
            targetId: employeeMap[rawName],
            amount: val,
            type,
            comment
          })
        })
      } else {
        // Для Аналитики (incomes / expenses)
        const amountCol = mapping.amount
        if (amountCol === undefined) return
        
        const valStr = String(row[amountCol] || '0')
        const val = parseFloat(valStr.replace(/[^\d.,-]/g, '').replace(',', '.'))
        if (val === 0 || isNaN(val)) return

        const payload = {
          id: idx,
          date,
          period_start: pStart,
          period_end: pEnd,
          amount: val,
          comment
        }

        // Доп поля
        if (targetTable === 'wb_incomes') {
          payload.source = mapping.source ? String(row[mapping.source] || '') : (customValues.source || 'Wildberries')
          payload.bonus = mapping.bonus ? parseFloat(String(row[mapping.bonus] || '0').replace(/[^\d.,-]/g, '').replace(',', '.')) : 0
          payload.penalty = mapping.penalty ? parseFloat(String(row[mapping.penalty] || '0').replace(/[^\d.,-]/g, '').replace(',', '.')) : 0
          payload.turnover = mapping.turnover ? parseFloat(String(row[mapping.turnover] || '0').replace(/[^\d.,-]/g, '').replace(',', '.')) : 0
          payload.sale_by_tariff = mapping.sale_by_tariff ? parseFloat(String(row[mapping.sale_by_tariff] || '0').replace(/[^\d.,-]/g, '').replace(',', '.')) : 0
        } else if (targetTable === 'expenses') {
          payload.category = mapping.category ? String(row[mapping.category] || '') : (customValues.category || 'Прочее')
        }

        finalized.push(payload)
      }
    })

    setPreviewData(finalized)
    setFilterType('all')

    if (finalized.length > 0) {
      const dates = finalized.map(d => d.date || d.period_start).filter(Boolean).sort()
      if (dates.length > 0) {
        const start = dates[0]
        const end = dates[dates.length - 1]
        
        try {
          let existing = []
          if (targetTable === 'wb_incomes') {
            existing = await api.getWbIncomes(pvzId, start, end)
          } else if (targetTable === 'expenses') {
            existing = await api.getExpenses(pvzId, start, end)
          } else if (targetTable === 'work_logs') {
            existing = await api.getWorkLogs('all', { startDate: start, endDate: end, pvzId })
          } else if (targetTable === 'payments') {
            existing = await api.getPayments({ periodStart: start, periodEnd: end, pvzId })
          }
          setDbItems(existing || [])
        } catch (e) {
          console.error('Ошибка при загрузке существующих записей:', e)
        }
      }
    } else {
      setDbItems([])
    }

    setLoadingExisting(false)
    setStep('review')
  }

  // 5. Импорт
  const handleImport = async () => {
    setLoading(true)
    try {
      if (targetTable === 'work_logs' || targetTable === 'payments') {
        const nameToId = { ...employeeMap }
        for (const name in nameToId) {
          if (nameToId[name] === 'new') {
            const newProfile = await api.createLegacyProfile(name, profile.org_id)
            nameToId[name] = newProfile.id
          }
        }

        // Если overwrite, удаляем существующие дубликаты из БД перед добавлением новых
        if (duplicateAction === 'overwrite' && duplicates.length > 0) {
          const logIdsToDelete = []
          const paymentIdsToDelete = []

          duplicates.forEach(d => {
            const empId = nameToId[d.employeeName]
            if (d.type === 'fines' || d.type === 'bonuses') {
              const dbType = d.type === 'fines' ? 'fine' : 'bonus'
              const found = dbItems.find(db => 
                db.employee_id === empId && 
                db.period_start === d.date && 
                db.type === dbType && 
                Number(db.amount) === Number(d.amount)
              )
              if (found) paymentIdsToDelete.push(found.id)
            } else {
              const found = dbItems.find(db => 
                db.employee_id === empId && 
                db.work_date === d.date && 
                db.type === d.type
              )
              if (found) logIdsToDelete.push(found.id)
            }
          })

          if (logIdsToDelete.length > 0) {
            await Promise.all(logIdsToDelete.map(id => api.deleteWorkLog(id)))
          }
          if (paymentIdsToDelete.length > 0) {
            await Promise.all(paymentIdsToDelete.map(id => api.deletePayment(id)))
          }
        }

        const logsToInsert = []
        const paymentsToInsert = []
        let skipped = 0

        previewData.filter(d => d.targetId !== 'skip').forEach(d => {
          const empId = nameToId[d.employeeName]
          const isDup = duplicates.some(dup => dup.id === d.id)

          if (isDup && duplicateAction === 'skip') {
            skipped++
            return
          }

          if (d.type === 'fines' || d.type === 'bonuses') {
            const dbType = d.type === 'fines' ? 'fine' : 'bonus'
            paymentsToInsert.push({
              employee_id: empId,
              type: dbType,
              amount: d.amount,
              period_start: d.date,
              period_end: d.date,
              comment: `${d.comment || ''} [Импорт: ${importId}]`.trim(),
              status: 'approved'
            })
          } else {
            logsToInsert.push({
              employee_id: empId,
              type: d.type,
              work_date: d.date,
              count: d.amount,
              comment: `${d.comment || ''} [Импорт: ${importId}]`.trim()
            })
          }
        })

        if (logsToInsert.length > 0) await api.bulkCreateWorkLogs(logsToInsert)
        if (paymentsToInsert.length > 0) await api.bulkCreatePayments(paymentsToInsert)
        toast.success(`Успешно загружено ${logsToInsert.length + paymentsToInsert.length} записей` + (skipped > 0 ? ` (пропущено дубликатов: ${skipped})` : ''))
      } else {
        // Для wb_incomes и expenses
        const itemsToInsert = []
        let skipped = 0

        // Если overwrite, удаляем дубликаты
        if (duplicateAction === 'overwrite' && duplicates.length > 0) {
          const idsToDelete = []
          duplicates.forEach(d => {
            if (targetTable === 'wb_incomes') {
              const found = dbItems.find(db => 
                db.period_start === d.period_start && 
                db.period_end === d.period_end && 
                (db.source || 'Wildberries') === (d.source || 'Wildberries')
              )
              if (found) idsToDelete.push(found.id)
            } else if (targetTable === 'expenses') {
              const found = dbItems.find(db => 
                db.date === (d.date || d.period_end) && 
                Number(db.amount) === Number(d.amount) && 
                db.category === d.category
              )
              if (found) idsToDelete.push(found.id)
            }
          })

          if (idsToDelete.length > 0) {
            if (targetTable === 'wb_incomes') {
              await Promise.all(idsToDelete.map(id => api.deleteWbIncome(id)))
            } else {
              await Promise.all(idsToDelete.map(id => api.deleteExpense(id)))
            }
          }
        }

        previewData.forEach(d => {
          const isDup = duplicates.some(dup => dup.id === d.id)
          if (isDup && duplicateAction === 'skip') {
            skipped++
            return
          }

          if (targetTable === 'wb_incomes') {
            itemsToInsert.push({
              pvz_id: pvzId,
              date: d.date || d.period_start,
              period_start: d.period_start,
              period_end: d.period_end,
              amount: d.amount,
              source: d.source,
              bonus: d.bonus || 0,
              penalty: d.penalty || 0,
              comment: `${d.comment || ''} [Импорт: ${importId}]`.trim(),
              turnover: d.turnover || 0,
              sale_by_tariff: d.sale_by_tariff || 0
            })
          } else if (targetTable === 'expenses') {
            itemsToInsert.push({
              pvz_id: pvzId,
              date: d.date || d.period_end,
              amount: d.amount,
              category: d.category,
              comment: `${d.comment || ''} [Импорт: ${importId}]`.trim(),
              is_paid: true
            })
          }
        })

        if (itemsToInsert.length > 0) {
          if (targetTable === 'wb_incomes') {
            await api.bulkAddIncomes(itemsToInsert)
          } else {
            await api.bulkAddExpenses(itemsToInsert)
          }
        }
        toast.success(`Успешно загружено ${itemsToInsert.length} записей` + (skipped > 0 ? ` (пропущено дубликатов: ${skipped})` : ''))
      }
      
      onComplete(importId)
      onClose()
    } catch (err) {
      console.error(err)
      toast.error('Ошибка: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const parseExcelDate = (val, defYear) => {
    if (!val) return new Date().toISOString().split('T')[0]
    
    // Если это число Excel
    if (typeof val === 'number') {
      const d = new Date(Math.round((val - 25569) * 86400 * 1000))
      return d.toISOString().split('T')[0]
    }

    if (typeof val === 'string') {
      // Пытаемся разбить по разделителям
      const parts = val.trim().split(/[./-]/)
      
      if (parts.length === 2) {
        // Формат ДД.ММ -> добавляем год
        const d = parts[0].padStart(2, '0')
        const m = parts[1].padStart(2, '0')
        const date = new Date(`${defYear}-${m}-${d}`)
        if (!isNaN(date)) return date.toISOString().split('T')[0]
      }

      if (parts.length === 3) {
        let y = parts[2], m = parts[1], d = parts[0]
        if (y.length === 2) y = '20' + y
        // Если вдруг формат ГГГГ.ММ.ДД
        if (parts[0].length === 4) {
          y = parts[0]; m = parts[1]; d = parts[2];
        }
        const date = new Date(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`)
        if (!isNaN(date)) return date.toISOString().split('T')[0]
      }
    }
    
    return new Date().toISOString().split('T')[0]
  }

  const goBack = () => {
    if (step === 'sheet') setStep('upload')
    if (step === 'columns') setStep('sheet')
    if (step === 'matching') setStep('columns')
    if (step === 'review') setStep('matching')
  }

  const formatDate = (iso) => {
    if (!iso) return '...'
    return new Date(iso).toLocaleDateString('ru-RU')
  }

  const renderTablePreview = () => {
    if (rawRows.length === 0) return null
    const previewRows = rawRows.slice(headerRow - 1, headerRow + 5)
    return (
      <div className="import-preview-table-wrapper" style={{ overflowX: 'auto', marginBottom: '20px', border: '1px solid var(--surface-border)', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              {previewRows[0]?.map((cell, idx) => (
                <th key={idx} style={{ padding: '8px', border: '1px solid var(--surface-border)', textAlign: 'left' }}>
                  {String(cell || `Кол. ${idx + 1}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.slice(1).map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, cellIdx) => {
                  let displayVal = String(cell)
                  // Если число похоже на дату Excel (40000-60000)
                  if (typeof cell === 'number' && cell > 40000 && cell < 60000) {
                    try {
                      displayVal = new Date(Math.round((cell - 25569) * 86400 * 1000)).toLocaleDateString('ru-RU')
                    } catch(e) {}
                  }
                  return (
                    <td key={cellIdx} style={{ padding: '8px', border: '1px solid var(--surface-border)' }}>
                      {displayVal}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Умный импорт из Excel" isBottomSheet={true}>
      <div className="import-wizard" style={{ paddingBottom: '20px' }}>
        
        {/* Индикатор шагов */}
        <div className="import-steps-indicator" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', padding: '0 4px' }}>
          {[1,2,3,4,5].map(n => (
            <div key={n} style={{ flex: 1, height: '4px', background: n <= stepInfo[step].num ? 'var(--wb-primary)' : 'var(--border-color)', borderRadius: '2px', margin: '0 2px' }} />
          ))}
        </div>
        
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
          Шаг {stepInfo[step].num}: {stepInfo[step].title}
        </h3>

        {step === 'upload' && (
          <div className="wizard-step">
            <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>Загрузите файл. Мы распознаем даты, даже если в них не указан год.</p>
            <div className="file-upload-box" style={{ border: '2px dashed var(--border-color)', borderRadius: '16px', padding: '40px', textAlign: 'center', cursor: 'pointer', position: 'relative' }}>
              <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
              <div style={{ fontSize: '40px', marginBottom: '10px' }}></div>
              <div style={{ fontWeight: '600' }}>Нажмите для выбора файла</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>Поддерживаются .xlsx, .xls, .csv</div>
            </div>
          </div>
        )}

        {step === 'sheet' && (
          <div className="wizard-step">
            <div className="input-group">
              <CustomSelect
                label="Выберите лист (вкладку)"
                value={selectedSheet}
                onChange={val => setSelectedSheet(val)}
                options={sheetNames.map(s => ({
                  value: s,
                  label: s,
                }))}
              />
            </div>
            {renderTablePreview()}
            <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
              <div className="input-group" style={{ flex: 1 }}>
                <label>Строка заголовков</label>
                <input className="input" type="number" value={headerRow} onChange={e => setHeaderRow(parseInt(e.target.value) || 1)} />
              </div>
              <div className="input-group" style={{ flex: 1 }}>
                <label>Год (если нет в файле)</label>
                <input className="input" type="number" value={defaultYear} onChange={e => setDefaultYear(parseInt(e.target.value) || 2024)} />
              </div>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px', marginBottom: '16px' }}>
              Если в таблице даты вида «02.01», мы добавим к ним год {defaultYear}.
            </p>
            
            <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '8px', marginTop: '16px' }}>Фильтр по дате (опционально)</h4>
            <div className="input-group" style={{ marginBottom: '8px' }}>
              <DateRangeFilter
                startDate={importStartDate}
                endDate={importEndDate}
                onApply={(start, end) => {
                  setImportStartDate(start)
                  setImportEndDate(end)
                }}
                onReset={() => {
                  setImportStartDate('')
                  setImportEndDate('')
                }}
                showQuickButtons={true}
                shiftMarkers={importMarkers}
              />
            </div>
          </div>
        )}

        {step === 'columns' && (
          <div className="wizard-step">
            {renderTablePreview()}
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>Сопоставьте поля системы с колонками:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '350px', overflowY: 'auto', paddingRight: '4px' }}>
              {fields.map(field => (
                <div className="input-group" key={field.id}>
                  <label>{field.label} {field.required && <span style={{ color: 'var(--danger)' }}>*</span>}</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <CustomSelect
                      value={mapping[field.id]}
                      onChange={val => setMapping({...mapping, [field.id]: val})}
                      options={[
                        { value: '', label: '-- Вписать вручную --' },
                        ...columns.map(c => ({
                          value: c.index,
                          label: c.label,
                        })),
                      ]}
                    />
                    {mapping[field.id] === '' && (
                      <input 
                        type="text" 
                        className="input" 
                        style={{ flex: 1 }} 
                        placeholder="Введите значение..." 
                        value={customValues[field.id] || ''} 
                        onChange={e => setCustomValues({...customValues, [field.id]: e.target.value})} 
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 'matching' && (
          <div className="wizard-step">
            <p style={{ fontSize: '14px', marginBottom: '12px' }}>Найдено имен: {Object.keys(employeeMap).length}. Привяжите их к аккаунтам:</p>
            <div className="matching-list" style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {Object.keys(employeeMap).map(name => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'var(--bg-primary)', borderRadius: '12px' }}>
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: '600' }}>{name}</span>
                  <CustomSelect
                    value={employeeMap[name]} 
                    onChange={val => setEmployeeMap({...employeeMap, [name]: val})}
                    options={[
                      { value: 'new', label: '+ Создать архивный' },
                      { value: 'skip', label: 'Пропустить' },
                      ...employees.map(e => ({
                        value: e.id,
                        label: e.name,
                      })),
                    ]}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="wizard-step">
            {stats && (
              <div style={{ background: 'var(--wb-primary-bg)', padding: '16px', borderRadius: '12px', marginBottom: '20px', border: '1px solid var(--wb-primary)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', opacity: 0.7 }}>Всего записей</div>
                    <div style={{ fontSize: '18px', fontWeight: '700' }}>{stats.count} шт.</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', opacity: 0.7 }}>Период данных</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{formatDate(stats.start)} — {formatDate(stats.end)}</div>
                  </div>
                </div>
              </div>
            )}

            {loadingExisting ? (
              <div style={{ padding: '16px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--surface-border)', borderRadius: '12px', marginBottom: '20px', textAlign: 'center', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <span className="btn-spinner-inline" style={{width:'14px',height:'14px',marginRight:'6px'}} /> Проверка совпадений с базой данных...
              </div>
            ) : duplicates.length > 0 ? (
              <div style={{ 
                background: 'rgba(245, 158, 11, 0.06)', 
                border: '1px solid rgba(245, 158, 11, 0.2)', 
                padding: '16px', 
                borderRadius: '12px', 
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--warning)', fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>
                  <span></span>
                  <span>Обнаружены совпадения с базой ({duplicates.length} шт.)</span>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  В системе уже есть записи за этот период. Выберите действие:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0, fontSize: '13px', cursor: 'pointer', fontWeight: duplicateAction === 'skip' ? '600' : 'normal', color: duplicateAction === 'skip' ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    <input type="radio" name="dupAction" value="skip" checked={duplicateAction === 'skip'} onChange={() => setDuplicateAction('skip')} style={{ cursor: 'pointer' }} />
                    Пропустить дубликаты (рекомендуется)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0, fontSize: '13px', cursor: 'pointer', fontWeight: duplicateAction === 'overwrite' ? '600' : 'normal', color: duplicateAction === 'overwrite' ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    <input type="radio" name="dupAction" value="overwrite" checked={duplicateAction === 'overwrite'} onChange={() => setDuplicateAction('overwrite')} style={{ cursor: 'pointer' }} />
                    Перезаписать (заменить существующие новыми данными)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0, fontSize: '13px', cursor: 'pointer', fontWeight: duplicateAction === 'keep_both' ? '600' : 'normal', color: duplicateAction === 'keep_both' ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    <input type="radio" name="dupAction" value="keep_both" checked={duplicateAction === 'keep_both'} onChange={() => setDuplicateAction('keep_both')} style={{ cursor: 'pointer' }} />
                    Загрузить как копии (добавить к имеющимся)
                  </label>
                </div>
              </div>
            ) : dbItems.length > 0 ? (
              <div style={{ 
                background: 'rgba(16, 185, 129, 0.06)', 
                border: '1px solid rgba(16, 185, 129, 0.2)', 
                padding: '12px 16px', 
                borderRadius: '12px', 
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                color: 'var(--success)',
                fontWeight: 500
              }}>
                <span></span>
                <span>Новые данные! Совпадений с базой данных за выбранный период не обнаружено.</span>
              </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Предпросмотр (первые 50 строк):</p>
              
              <div style={{ display: 'flex', gap: '4px' }}>
                <button 
                  type="button"
                  className={`btn btn-sm`} 
                  onClick={() => setFilterType('all')}
                  style={{ 
                    fontSize: '11px', 
                    padding: '3px 8px', 
                    height: 'auto', 
                    borderRadius: '6px',
                    background: filterType === 'all' ? 'var(--wb-primary)' : 'var(--bg-secondary)',
                    color: filterType === 'all' ? '#fff' : 'var(--text-secondary)',
                    border: '1px solid var(--border-color)',
                    cursor: 'pointer'
                  }}
                >
                  Все ({previewData.length})
                </button>
                <button 
                  type="button"
                  className={`btn btn-sm`} 
                  onClick={() => setFilterType('new')}
                  style={{ 
                    fontSize: '11px', 
                    padding: '3px 8px', 
                    height: 'auto', 
                    borderRadius: '6px',
                    background: filterType === 'new' ? 'rgba(16, 185, 129, 0.2)' : 'var(--bg-secondary)',
                    color: filterType === 'new' ? 'var(--success)' : 'var(--text-secondary)',
                    borderColor: filterType === 'new' ? 'var(--success)' : 'var(--border-color)',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    fontWeight: filterType === 'new' ? 'bold' : 'normal',
                    cursor: 'pointer'
                  }}
                >
                  Новые ({previewData.length - duplicates.length})
                </button>
                {duplicates.length > 0 && (
                  <button 
                    type="button"
                    className={`btn btn-sm`} 
                    onClick={() => setFilterType('dup')}
                    style={{ 
                      fontSize: '11px', 
                      padding: '3px 8px', 
                      height: 'auto', 
                      borderRadius: '6px',
                      background: filterType === 'dup' ? 'rgba(245, 158, 11, 0.2)' : 'var(--bg-secondary)',
                      color: filterType === 'dup' ? 'var(--warning)' : 'var(--text-secondary)',
                      borderColor: filterType === 'dup' ? 'var(--warning)' : 'var(--border-color)',
                      borderStyle: 'solid',
                      borderWidth: '1px',
                      fontWeight: filterType === 'dup' ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    Совпадения ({duplicates.length})
                  </button>
                )}
              </div>
            </div>

            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '10px' }}>Дата / Период</th>
                    <th style={{ textAlign: 'left', padding: '10px' }}>Объект / Коммент</th>
                    <th style={{ textAlign: 'right', padding: '10px' }}>Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPreviewData.slice(0, 50).map(d => {
                    const isDup = duplicates.some(dup => dup.id === d.id)
                    return (
                      <tr 
                        key={d.id} 
                        style={{ 
                          borderBottom: '1px solid var(--border-color)',
                          background: isDup ? 'rgba(245, 158, 11, 0.04)' : 'transparent',
                        }}
                      >
                        <td style={{ padding: '8px 10px', verticalAlign: 'middle' }}>
                          {d.period_start ? `${formatDate(d.period_start)} - ${formatDate(d.period_end)}` : formatDate(d.date)}
                        </td>
                        <td style={{ padding: '8px 10px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600 }}>{d.employeeName || d.category || d.source || '---'}</span>
                            {isDup ? (
                              <span style={{ fontSize: '9px', background: 'rgba(245, 158, 11, 0.15)', color: 'var(--warning)', padding: '1px 5px', borderRadius: '4px', fontWeight: 'bold' }}>
                                Есть в базе
                              </span>
                            ) : (
                              <span style={{ fontSize: '9px', background: 'rgba(16, 185, 129, 0.15)', color: 'var(--success)', padding: '1px 5px', borderRadius: '4px', fontWeight: 'bold' }}>
                                Новая
                              </span>
                            )}
                          </div>
                          {d.comment && <div style={{ fontSize: '10px', opacity: 0.6 }}>{d.comment}</div>}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold', verticalAlign: 'middle', color: isDup ? 'var(--warning)' : 'var(--text-primary)' }}>
                          {d.amount.toLocaleString()} ₽
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Навигация */}
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
          {step !== 'upload' && (
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={goBack}>
              ← Назад
            </button>
          )}
          {step !== 'upload' && step !== 'review' && (
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={
              step === 'sheet' ? handleSheetSelect : 
              step === 'columns' ? handleMappingConfirm : 
              handleMatchingConfirm
            }>
              Далее →
            </button>
          )}
          {step === 'review' && (
            <button className="btn btn-primary" style={{ flex: 2 }} onClick={handleImport} disabled={loading}>
              {loading ? <><span className="btn-spinner-inline" style={{width:'14px',height:'14px',marginRight:'6px'}} /> Загрузка...</> : 'Подтвердить и Импортировать'}
            </button>
          )}
        </div>

        {step === 'upload' && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button className="btn btn-ghost" onClick={onClose}>Отмена</button>
          </div>
        )}
      </div>
    </Modal>
  )
}
